import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { FunnelEntry, RequirementStatusCategory } from '../../models/dashboard.model';

export interface ProjectFunnel {
  projectId: string;
  name: string;
  entries: FunnelEntry[];
  withoutStatusCount: number;
}

const CATEGORY_COLOR_VARS: Readonly<Record<RequirementStatusCategory, string>> = {
  OPEN: '--to-slate-dot',
  IN_PROGRESS: '--to-warn-border',
  REVIEW: '--to-blue-dot',
  APPROVED: '--to-green-dot',
  DONE: '--to-purple-dot',
};

const LEGACY_COLOR_VAR = '--to-slate-dot';

const PROJECT_COLOR_VARS = [
  '--to-blue-dot',
  '--to-purple-dot',
  '--to-green-dot',
  '--to-warn-border',
  '--to-cyan-dot',
] as const;

interface FunnelTile {
  status: string;
  count: number;
  label: string;
  tint: string;
  border: string;
  dot: string;
}

interface ProjectGroup {
  projectId: string;
  name: string;
  colorVar: string;
  requirementsLink: unknown[];
  tiles: FunnelTile[];
  withoutStatusCount: number;
}

@Component({
  selector: 'app-requirements-funnel',
  imports: [RouterLink, TranslatePipe, MatIcon, SectionTitleComponent],
  templateUrl: './requirements-funnel.component.html',
  styleUrl: './requirements-funnel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementsFunnelComponent {
  projects = input.required<ProjectFunnel[]>();

  protected readonly headingId = `requirements-funnel-heading-${crypto.randomUUID()}`;

  protected readonly groups = computed<ProjectGroup[]>(() =>
    this.projects().map((project, index) => ({
      projectId: project.projectId,
      name: project.name,
      colorVar: PROJECT_COLOR_VARS[index % PROJECT_COLOR_VARS.length],
      requirementsLink: ['/projects', project.projectId, 'requirements'],
      tiles: project.entries.map((entry) => this.toTile(entry)),
      withoutStatusCount: project.withoutStatusCount,
    })),
  );

  private toTile(entry: FunnelEntry): FunnelTile {
    const colorVar = entry.category ? CATEGORY_COLOR_VARS[entry.category] : LEGACY_COLOR_VAR;
    return {
      status: entry.status,
      count: entry.count,
      label: entry.status,
      tint: `color-mix(in srgb, var(${colorVar}) 8%, transparent)`,
      border: `color-mix(in srgb, var(${colorVar}) 30%, transparent)`,
      dot: `var(${colorVar})`,
    };
  }
}
